import type { ComponentProps } from 'react';
import {
  Link as RouterLink,
  Navigate as RouterNavigate,
  type NavigateFunction,
  type NavigateOptions,
  type To,
  useLocation,
  useNavigate as useRouterNavigate,
} from 'react-router-dom';
import { DEFAULT_LOCALE } from './constants';
import { getLocaleFromPathname, getLocalizedPath } from './routing';

function shouldLocalizePath(to: string) {
  return to.startsWith('/');
}

export function useLocalizedPath() {
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname) ?? DEFAULT_LOCALE;

  return (to: string) => (shouldLocalizePath(to) ? getLocalizedPath(to, locale) : to);
}

type LocalizedLinkProps = ComponentProps<typeof RouterLink>;

export function Link({ to, ...props }: LocalizedLinkProps) {
  const localizePath = useLocalizedPath();

  return <RouterLink to={typeof to === 'string' ? localizePath(to) : to} {...props} />;
}

type LocalizedNavigateProps = ComponentProps<typeof RouterNavigate>;

export function Navigate({ to, ...props }: LocalizedNavigateProps) {
  const localizePath = useLocalizedPath();

  return <RouterNavigate to={typeof to === 'string' ? localizePath(to) : to} {...props} />;
}

export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  const localizePath = useLocalizedPath();

  const localizedNavigate = (to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      return navigate(to);
    }

    if (typeof to === 'string') {
      return navigate(localizePath(to), options);
    }

    return navigate(to, options);
  };

  return localizedNavigate as NavigateFunction;
}
